'use client';

import { useEffect, useRef } from 'react';
import {
    DEFAULT_INVITEE_REWARD,
    clearPendingReferralCode,
    didReferralClaimSucceed,
    getPendingInviteeReward,
    getInviteeRewardFromSearch,
    getPendingReferralCode,
    getReferralClaimResult,
    getReferralCodeFromSearch,
    hasClaimedReferralCode,
    setClaimedReferralCode,
    setReferralClaimResult,
} from "@/features/referral/shared";
import { navigateToEnterApp } from '@/contexts/auth/auth-navigation';

interface ReferralApi {
    billing: {
        claimCredits(inviteCode: string): Promise<{ code: number }>;
    };
}

interface ReferralLoginCompletionOptions {
    isAuthenticated: boolean;
    isLoading: boolean;
    userSub?: string;
    api: ReferralApi;
    showSuccess: (message: string) => void;
    showInfo: (message: string) => void;
}

interface ReferralLoginCompletionResult {
    shouldShowLoading: boolean;
}

export function useReferralLoginCompletion({
    isAuthenticated,
    isLoading,
    userSub,
    api,
    showSuccess,
    showInfo,
}: ReferralLoginCompletionOptions): ReferralLoginCompletionResult {
    const hasRedirectedRef = useRef(false);
    const claimStartedRef = useRef<string | null>(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const redirectToWorkspace = () => {
            if (hasRedirectedRef.current) return;
            hasRedirectedRef.current = true;
            navigateToEnterApp();
        };

        if (!isLoading && isAuthenticated && !hasRedirectedRef.current) {
            const inviteCode =
                getReferralCodeFromSearch(window.location.search) ||
                getPendingReferralCode();
            const inviteeReward =
                getInviteeRewardFromSearch(window.location.search) ??
                getPendingInviteeReward() ??
                DEFAULT_INVITEE_REWARD;

            if (inviteCode && userSub) {

                const lastResult = getReferralClaimResult();
                if (didReferralClaimSucceed(lastResult, userSub, inviteCode)) {
                    redirectToWorkspace();
                    return;
                }

                if (
                    hasClaimedReferralCode(userSub, inviteCode) ||
                    claimStartedRef.current === `${userSub}:${inviteCode}`
                ) {
                    redirectToWorkspace();
                    return;
                }

                claimStartedRef.current = `${userSub}:${inviteCode}`;

                void api.billing.claimCredits(inviteCode)
                    .then((response) => {
                        if (response.code === 0 || response.code === 200) {
                            setReferralClaimResult({
                                status: 'success',
                                userId: userSub,
                                inviteCode,
                                response,
                            });
                            setClaimedReferralCode(userSub, inviteCode);
                            showSuccess(
                                `${inviteeReward} credits have been added to your account`
                            );
                            return;
                        }

                        setReferralClaimResult({
                            status: 'error',
                            userId: userSub,
                            inviteCode,
                            response,
                        });
                        showInfo('Failed to claim credits');
                    })
                    .catch((error) => {
                        console.error('Failed to claim referral credits:', error);
                        setReferralClaimResult({
                            status: 'error',
                            userId: userSub,
                            inviteCode,
                            error,
                        });
                        showInfo(error?.message || 'Failed to claim credits');
                    })
                    .finally(() => {
                        clearPendingReferralCode();
                        redirectToWorkspace();
                    });
                return;
            }

            redirectToWorkspace();
        }
    }, [api, isAuthenticated, isLoading, showInfo, showSuccess, userSub]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handlePageShow = (event: PageTransitionEvent) => {
            if (!event.persisted) return;

            hasRedirectedRef.current = false;
            if (!isLoading && isAuthenticated) {
                hasRedirectedRef.current = true;
                navigateToEnterApp({ replace: true });
                return;
            }
        };

        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [isAuthenticated, isLoading]);

    return {
        shouldShowLoading: !isLoading && isAuthenticated,
    };
}
